import { t } from "@lingui/core/macro";
import graphql from "babel-plugin-relay/macro";
import { useAtom } from "jotai";
import { Suspense, useCallback, useEffect, useMemo } from "react";
import { PreloadedQuery, usePreloadedQuery, useQueryLoader } from "react-relay";

import {
  facilityToScheduleOption,
  ScheduleOption,
  ScheduleSwitcher,
} from "@/components/ScheduleSwitcher";
import { useScheduleNavigator } from "@/hooks/useScheduleNavigator";
import { DropdownSelect } from "@whoosh/design-system/src/components/controls/DropdownSelect";
import { Loader } from "@whoosh/design-system/src/components/display";
import { VStack, Text } from "@whoosh/design-system/src/components/primitive";
import { formatHHMM } from "@whoosh/utilities/src/dates";

import {
  selectedFacilityId_atom,
  selectedPartySize_atom,
  selectedSeatingShift_atom,
} from "../../../../BookingDetail/ForYouDiningRemains/atoms";
import { DiningGroupSizeSelector } from "./DiningGroupSizeSelector";
import { DiningAgendaFiltersQuery } from "./__generated__/DiningAgendaFiltersQuery.graphql";

export type DiningSeatingShift = {
  name: string;
  startTime: string;
  endTime: string;
};

export type DiningAgendaFilterState = {
  selectedFacilityId: string | null;
  selectedPartySize: number;
  selectedSeatingShift: DiningSeatingShift | null;
};

export const DiningAgendaFilters = ({
  date,
  isMobile,
  filterState,
  onFilterStateChange,
}: {
  date: string;
  isMobile: boolean;
  filterState?: DiningAgendaFilterState;
  onFilterStateChange?: (state: DiningAgendaFilterState) => void;
}) => {
  const [filterQueryRef, loadFiltersQuery] =
    useQueryLoader<DiningAgendaFiltersQuery>(DiningAgendaFiltersQueryString);

  useEffect(() => {
    loadFiltersQuery({ date: date });
  }, [date, loadFiltersQuery]);

  return (
    <Suspense fallback={<Loader />}>
      {filterQueryRef ? (
        <DiningAgendaFiltersInternal
          date={date}
          filterState={filterState}
          queryRef={filterQueryRef}
          isMobile={isMobile}
          onFilterStateChange={onFilterStateChange}
        />
      ) : null}
    </Suspense>
  );
};

type Props = {
  date: string;
  filterState?: DiningAgendaFilterState;
  queryRef: PreloadedQuery<DiningAgendaFiltersQuery>;
  isMobile: boolean;
  onFilterStateChange?: (state: DiningAgendaFilterState) => void;
};

const DiningAgendaFiltersInternal = ({
  date,
  filterState,
  queryRef,
  isMobile,
  onFilterStateChange,
}: Props) => {
  const [atomSelectedShift, setAtomSelectedShift] = useAtom(
    selectedSeatingShift_atom
  );
  const [atomSelectedFacilityId, setAtomSelectedFacilityId] = useAtom(
    selectedFacilityId_atom
  );
  const [atomSelectedPartySize] = useAtom(selectedPartySize_atom);
  const isControlled = !!filterState && !!onFilterStateChange;
  const selectedShift = isControlled
    ? filterState?.selectedSeatingShift ?? null
    : atomSelectedShift;
  const selectedFacilityId = isControlled
    ? filterState?.selectedFacilityId ?? null
    : atomSelectedFacilityId;
  const selectedPartySize = isControlled
    ? filterState?.selectedPartySize
    : atomSelectedPartySize;

  const data = usePreloadedQuery(DiningAgendaFiltersQueryString, queryRef);
  const club = data.session?.currentClientProfile?.member?.club;
  const navigateToSchedule = useScheduleNavigator();

  const updateFilterState = useCallback(
    (updates: Partial<DiningAgendaFilterState>) => {
      if (isControlled && filterState && onFilterStateChange) {
        onFilterStateChange({ ...filterState, ...updates });
      }
    },
    [filterState, isControlled, onFilterStateChange]
  );

  const diningFacilities = useMemo(
    () => club?.diningFacilities?.edges || [],
    [club?.diningFacilities?.edges]
  );

  const selectedFacility = diningFacilities.find(
    (facility) => facility?.node?.id === selectedFacilityId
  )?.node;

  useEffect(() => {
    if (!selectedFacility && diningFacilities.length) {
      const defaultFacility = diningFacilities[0];
      const defaultFacilityId = defaultFacility?.node?.id || "";

      if (isControlled) {
        updateFilterState({ selectedFacilityId: defaultFacilityId });
      } else {
        setAtomSelectedFacilityId(defaultFacilityId);
      }
    }
  }, [
    diningFacilities,
    isControlled,
    selectedFacility,
    setAtomSelectedFacilityId,
    updateFilterState,
  ]);

  const facilityOptions = diningFacilities.map((f) => ({
    label: f?.node?.name || "",
    value: f?.node?.id || "",
  }));

  const shiftOptions = useMemo(
    () =>
      diningFacilities
        ?.find((f) => f?.node?.id === selectedFacilityId)
        ?.node?.shifts?.edges?.map((s) => ({
          label: getShiftName(s?.node?.name, s?.node?.startAt, s?.node?.endAt),
          value: [s?.node?.name, s?.node?.startAt, s?.node?.endAt].join(","),
        })) || [],
    [diningFacilities, selectedFacilityId]
  );

  const handleFacilityChange = (facilityId: string | null) => {
    if (isControlled) {
      updateFilterState({
        selectedFacilityId: facilityId,
        selectedSeatingShift: null,
      });
    } else {
      setAtomSelectedFacilityId(facilityId);
      setAtomSelectedShift(null);
    }
  };

  const handleShiftChange = (shiftString: string) => {
    const [name, startAt, endAt] = shiftString.split(",");
    const nextShift = {
      name: name || "",
      startTime: startAt || "",
      endTime: endAt || "",
    };

    if (isControlled) {
      updateFilterState({ selectedSeatingShift: nextShift });
    } else {
      setAtomSelectedShift(nextShift);
    }
  };

  const selectedScheduleOption = facilityToScheduleOption(
    selectedFacility ?? null
  );

  const handleScheduleSwitcherChange = (option: ScheduleOption) => {
    navigateToSchedule(option.type, option.slug, date);
  };

  const shiftString = useMemo(
    () =>
      selectedShift
        ? [
            selectedShift.name,
            selectedShift.startTime,
            selectedShift.endTime,
          ].join(",")
        : null,
    [selectedShift]
  );

  return (
    <VStack gap={4}>
      {isMobile ? (
        <ScheduleSwitcher
          selectedOption={selectedScheduleOption}
          sessionRef={data.session ?? null}
          onChange={handleScheduleSwitcherChange}
        />
      ) : null}

      {/* Location */}
      {facilityOptions.length > 1 ? (
        <VStack gap={4}>
          <Text
            maxFontSizeMultiplier={2}
            variant="mainSmallSemiBold"
          >{t`Location`}</Text>
          <DropdownSelect
            options={facilityOptions}
            onChange={handleFacilityChange}
            value={selectedFacilityId || ""}
          />
        </VStack>
      ) : null}

      <DiningGroupSizeSelector
        selectedGroupSize={selectedPartySize}
        onChange={
          isControlled
            ? (groupSize) => updateFilterState({ selectedPartySize: groupSize })
            : undefined
        }
      />

      {/* Seating */}
      <VStack gap={4}>
        <Text
          maxFontSizeMultiplier={2}
          variant="mainSmallSemiBold"
        >{t`Seating`}</Text>
        <DropdownSelect
          placeholder={t`Select a Seating Time`}
          options={shiftOptions}
          onChange={(val) => {
            if (val) {
              handleShiftChange(val);
            }
          }}
          value={shiftString}
        />
      </VStack>
    </VStack>
  );
};

export const DiningAgendaFiltersQueryString = graphql`
  query DiningAgendaFiltersQuery($date: Date!) {
    session {
      ...ScheduleSwitcher_session
      currentClientProfile {
        member {
          club {
            diningFacilities: facilities(type: [DINING]) {
              edges {
                node {
                  ...facilityToScheduleOption_facility
                  id
                  name
                  shifts(date: $date) {
                    edges {
                      node {
                        id
                        startAt
                        endAt
                        name
                        date
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`;

const getShiftName = (
  name: string | undefined,
  startAt: string | undefined,
  endAt: string | undefined
) => {
  const startTime = formatHHMM(startAt || "");
  const endTime = formatHHMM(endAt || "");
  return `${name} (${startTime} - ${endTime})`;
};
